<template>
  <div class="rds-page">

    <!-- Active section title -->
    <FiPageRail :title="pageTitle" />

    <!-- Category filter pills -->
    <div class="rds-pill-bar">
      <button
        v-for="tab in visibleTabs"
        :key="tab.id"
        class="rds-pill"
        :class="{ active: activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        <span v-if="tab.id === 'favorites'" class="rds-pill-star">★</span>
        {{ tab.label }}
      </button>
    </div>

    <!-- Favorites tab -->
    <div v-if="activeTab === 'favorites'" class="rds-section">
      <p class="rds-section-sub">Your starred reports — click the star on any report card to add it here.</p>
      <div v-if="favoriteCards.length" class="rds-card-grid">
        <div v-for="card in favoriteCards" :key="card.route" class="rds-card-wrap">
          <button class="rds-star rds-star--active" :title="'Unfavorite ' + card.name" @click.prevent="toggleFavorite(card.route)">★</button>
          <router-link :to="{ name: card.route }" class="rds-card">
            <div class="rds-card-title">{{ card.name }}</div>
            <div class="rds-card-desc">{{ card.description }}</div>
          </router-link>
        </div>
      </div>
      <div v-else class="rds-empty-favorites">
        <span class="rds-empty-star">☆</span>
        <p>No favorites yet. Click the <strong>★</strong> on any report card to save it here.</p>
      </div>
    </div>

    <!-- Category sections: one for the selected pill, or every visible
         section stacked when the default All pill is active -->
    <div v-for="sec in displaySections" :key="sec.id" class="rds-section">
      <h3 v-if="activeTab === 'all'" class="rds-section-heading">{{ sec.title }}</h3>
      <p class="rds-section-sub">{{ sec.sub }}</p>
      <div class="rds-card-grid">
        <div v-for="card in sec.cards" :key="card.route" class="rds-card-wrap">
          <button
            class="rds-star"
            :class="{ 'rds-star--active': isFavorite(card.route) }"
            :title="isFavorite(card.route) ? 'Unfavorite' : 'Favorite'"
            @click.prevent="toggleFavorite(card.route)"
          >{{ isFavorite(card.route) ? '★' : '☆' }}</button>
          <router-link :to="{ name: card.route }" class="rds-card">
            <div class="rds-card-title">{{ card.name }}</div>
            <div class="rds-card-desc">{{ card.description }}</div>
          </router-link>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { favoritesState, isFavorite, toggleFavorite } from '@/utils/report_favorites'

export default {
  name: 'report_dashboard_shell',
  data() {
    return {
      activeTab: null,
      favoritesState,
    }
  },
  methods: {
    isFavorite(route) {
      return isFavorite(route)
    },
    toggleFavorite(route) {
      toggleFavorite(route)
    },
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    tenant_type() { return this.user.types[this.user.tenant] },
    telematics_type() { return this.user.telematics_types[this.user.tenant] },
    features() { return this.user.features[this.user.tenant] || {} },
    isEffectiveAdmin() { return this.user.role === 'admin' && this.viewAs !== 'non-admin' },
    isTbn() { return this.tenant_type === 'TBN' },
    isCmFleet() { return this.tenant_type === 'CM-FLEET' },
    isParentTbn() { return this.isTbn && this.user.tenant.indexOf('parent') !== -1 },
    restrictedRole() {
      if (this.isEffectiveAdmin) return null
      if (this.isCmFleet) return null
      const r = this.user.role
      if (r === 'garage') return 'garage'
      if (this.isParentTbn && r !== 'customer-admin') return 'tbn-parent'
      if (r === 'manager') return 'manager'
      return null
    },
    showActuals() {
      if (this.restrictedRole === 'garage') return false
      if (this.isCmFleet) return false
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') return true
      return !!this.features.actuals_enabled
    },
    showPlanned() {
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') return false
      if (this.isCmFleet) return false
      return true
    },
    showOtp() {
      if (this.restrictedRole) return false
      return !!this.features.views_reports_otp_report_enabled || this.isCmFleet
    },
    showMisc() {
      if (this.restrictedRole === 'garage' || this.restrictedRole === 'manager') return false
      if (this.isCmFleet) return false
      return true
    },
    allCards() {
      return [
        ...this.actualsCards,
        ...this.plannedCards,
        ...this.otpCards,
        ...this.miscCards,
      ]
    },
    favoriteCards() {
      return this.allCards.filter(c => this.favoritesState.routes.includes(c.route))
    },
    visibleTabs() {
      // All leads so the immediate watcher below lands on it by default
      const tabs = [{ id: 'all', label: 'All' }, { id: 'favorites', label: 'Favorites' }]
      if (this.showActuals) tabs.push({ id: 'actuals', label: 'Actuals' })
      if (this.showPlanned) tabs.push({ id: 'planned', label: 'Planned' })
      if (this.showOtp)     tabs.push({ id: 'otp',     label: 'On-Time' })
      if (this.showMisc)    tabs.push({ id: 'misc',    label: 'Other' })
      return tabs
    },
    pageTitle() {
      if (this.activeTab === 'all') return 'All Reports'
      if (this.activeTab === 'favorites') return 'Favorite Reports'
      const sec = this.categorySections.find(s => s.id === this.activeTab)
      return sec ? sec.title : 'Reports'
    },
    categorySections() {
      const secs = []
      if (this.showActuals) {
        secs.push({ id: 'actuals', title: 'Actuals Based Reports',
          sub: 'Reports generated from real-world actuals matched against planned bookings.', cards: this.actualsCards })
      }
      if (this.showPlanned) {
        secs.push({ id: 'planned', title: 'Planned Data Based Reports',
          sub: 'Reports derived from scheduled bookings and planned fleet utilisation.', cards: this.plannedCards })
      }
      if (this.showOtp) {
        secs.push({ id: 'otp', title: 'On-Time and Route Reports',
          sub: 'On-time performance reports using route and telematics data.', cards: this.otpCards })
      }
      if (this.showMisc) {
        secs.push({ id: 'misc', title: 'Other Reports',
          sub: 'Additional fleet reports covering unplanned activity, maintenance, and event history.', cards: this.miscCards })
      }
      return secs
    },
    displaySections() {
      if (this.activeTab === 'all') {
        const secs = []
        if (this.favoriteCards.length) {
          secs.push({ id: 'favorites', title: 'Favorites', sub: 'Your starred reports.', cards: this.favoriteCards })
        }
        return secs.concat(this.categorySections)
      }
      const hit = this.categorySections.find(s => s.id === this.activeTab)
      return hit ? [hit] : []
    },
    actualsCards() {
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') {
        if (this.isTbn) {
          return [
            { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report_tbn', description: 'Driver activity and hours for a single day.' },
            { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn',description: 'Driver activity and hours across multiple days.' },
          ]
        }
        return [
          { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report', description: 'Driver activity and hours for a single day.' },
          { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report',description: 'Driver activity and hours across multiple days.' },
        ]
      }
      if (this.isTbn) {
        const tbnCards = [
          { name: 'Daily On-Time Report',       route: 'actuals_perf_report_tbn',           description: 'Compare planned booking times against actual on-time performance by day.' },
          { name: 'Multiday On-Time Report',    route: 'actuals_multiday_otp_report_tbn',   description: 'On-time performance trends across a custom date range.' },
          { name: 'Driver On-Time % Report', route: 'actuals_on_time_report_tbn',       description: 'On-time gates (to yard, depart yard) by driver for a date range.' },
          { name: 'Pickup/Dropoff Report',  route: 'actuals_billing_report_tbn',        description: 'Pickup and dropoff event details per booking.' },
          { name: 'Start Events Report',    route: 'actuals_start_events_report_tbn',   description: 'Vehicle start events matched to scheduled bookings.' },
          { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report_tbn', description: 'Driver activity and hours for a single day.' },
          { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn',description: 'Driver activity and hours across multiple days.' },
          { name: 'Payroll Summary Report', route: 'actuals_payroll_summary_report_tbn',description: 'Aggregated payroll totals by driver for a date range.' },
        ]
        // FI-2167: ebmeyer only, see nav/registry.js mileage_daily_tbn
        if (this.user.tenant === 'ebmeyer-08d0ce10') {
          tbnCards.push({ name: 'Actual Mileage Report', route: 'actuals_mileage_tbn', description: 'Miles per vehicle per day, from bookings vs. vehicle odometer, over a day or a range.' })
        }
        return tbnCards
      }
      const cards = [
        { name: 'Daily On-Time Report',       route: 'actuals_perf_report',           description: 'Compare planned booking times against actual on-time performance by day.' },
        { name: 'Multiday On-Time Report',    route: 'actuals_multiday_otp_report',   description: 'On-time performance trends across a custom date range.' },
        { name: 'Driver On-Time % Report', route: 'actuals_on_time_report',        description: 'On-time gates (to yard, depart yard, to pickup) by driver for a date range.' },
        { name: 'Mileage By Booking',     route: 'actuals_mileage_by_booking_report', description: 'Per-booking mileage breakdown for accurate client billing.' },
        { name: 'Billing Report',         route: 'actuals_billing_report',        description: 'Summarised billing data based on completed actuals.' },
        { name: 'Vehicle Start/Stop',     route: 'actuals_vehicle_report',        description: 'Vehicle engine start and stop events per booking.' },
        { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report', description: 'Driver activity and hours for a single day.' },
        { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report',description: 'Driver activity and hours across multiple days.' },
        { name: 'Payroll Summary Report', route: 'actuals_payroll_summary_report',description: 'Aggregated payroll totals by driver for a date range.' },
      ]
      return cards
    },
    plannedCards() {
      if (this.restrictedRole === 'garage') {
        return [{ name: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly', description: 'Weekly view of vehicle fleet utilisation.' }]
      }
      if (this.isTbn) {
        return [
          { name: 'Driver Utilization Daily',   route: 'planned_drivers_daily_tbn',          description: 'Daily view of driver utilisation against planned bookings.' },
          { name: 'Driver Utilization Weekly',  route: 'planned_drivers_weekly_util_tbn',     description: 'Weekly driver utilisation across the fleet.' },
          { name: 'Driver Utilization Monthly', route: 'planned_driver_types_monthly_tbn',    description: 'Monthly breakdown of driver type utilisation.' },
          { name: 'Driver Dispatch Weekly',     route: 'planned_drivers_weekly_dispatch_tbn', description: 'Weekly driver dispatch schedule.' },
          { name: 'Vehicle Utilization Daily',  route: 'planned_vehicle_daily_tbn',           description: 'Daily vehicle usage compared to planned schedule.' },
          { name: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly_tbn',          description: 'Weekly view of vehicle fleet utilisation.' },
          { name: 'Vehicle Utilization Monthly',route: 'planned_vehicle_types_monthly_tbn',   description: 'Monthly fleet utilisation by vehicle type.' },
          { name: 'Vehicle Departure Board',    route: 'trip_sheet_tbn',                      description: 'Scheduled departure times and vehicle assignments.' },
          { name: 'Booking Dispatch Board',     route: 'dispatch_board_tbn',                  description: 'Live dispatch board for booking management.' },
          { name: 'Booking Monthly View',       route: 'booking_monthly_view_tbn',            description: 'Monthly calendar view of all bookings.' },
        ]
      }
      return [
        { name: 'Driver Board',                route: 'driver_board',                    description: 'Real-time driver assignments and availability board.' },
        { name: 'Driver Utilization Daily',    route: 'planned_drivers_daily',           description: 'Daily view of driver utilisation against planned bookings.' },
        { name: 'Driver Utilization Monthly',  route: 'planned_driver_types_monthly_cm', description: 'Monthly breakdown of driver type utilisation.' },
        { name: 'Vehicle Utilization Daily',   route: 'planned_vehicle_daily',           description: 'Daily vehicle usage compared to planned schedule.' },
        { name: 'Vehicle Utilization Weekly',  route: 'planned_vehicle_weekly',          description: 'Weekly view of vehicle fleet utilisation.' },
        { name: 'Vehicle Utilization Monthly', route: 'planned_vehicle_types_monthly',   description: 'Monthly fleet utilisation by vehicle type.' },
        { name: 'Vehicle Departure Board',     route: 'trip_sheet',                      description: 'Scheduled departure times and vehicle assignments.' },
      ]
    },
    otpCards() {
      const cards = [
        { name: 'On-Time Route History',              route: 'vehicle_route_history', description: 'Historical route data for each vehicle.' },
        { name: 'Route Addresses',            route: 'route_addresses',       description: 'Address list for configured routes.' },
        { name: 'On-Time Nightly Run History', route: 'report_otp',            description: 'Nightly OTP report run history and results.' },
        { name: 'On-Time Results History',        route: 'otp_history',           description: 'Historical OTP results with trends over time.' },
      ]
      if (!this.isTbn) {
        cards.splice(1, 0, { name: 'Route Assignments', route: 'route_assignments', description: 'Route-to-vehicle assignment history.' })
      }
      return cards
    },
    miscCards() {
      if (this.restrictedRole === 'tbn-parent') {
        return [{ name: 'Event Notes History', route: 'event_notes_list', description: 'Notes and annotations recorded against fleet events.' }]
      }
      const cards = []
      if (!this.isTbn) {
        cards.push(
          { name: 'Unplanned Vehicle Activity', route: 'report_unplanned_vehicle', description: 'Vehicles that moved outside of scheduled bookings.' },
          { name: 'Unplanned Driver Activity',  route: 'report_unplanned_hos',     description: 'Driver activity recorded outside planned bookings.' },
        )
      }
      cards.push({ name: 'Event Notes History', route: 'event_notes_list', description: 'Notes and annotations recorded against fleet events.' })
      if (this.user.tenant === 'jeffersonlines-d637fdad') {
        cards.unshift(
          { name: 'Bus Tracker',             route: 'bus_tracker',              description: 'Live bus tracking view for Jefferson Lines.' },
          { name: 'Arrive/Depart Dashboard', route: 'arrivals_departures_board', description: 'Arrivals and departures live dashboard.' },
        )
      }
      if (this.isTbn || this.telematics_type === 'SAMSARA') {
        cards.push({ name: 'HOS Event History', route: 'hos_history_report', description: 'Hours of service events for equipped vehicles.' })
      }
      if (this.isTbn && this.features.tbn_tyler_route_sync_enabled) {
        cards.push({ name: 'Tyler Route Sync', route: 'tyler_route_processor', description: 'Process and sync Tyler route data.' })
      }
      if (this.features.vehicle_status_management_enabled) {
        cards.push({ name: 'Vehicle Status History', route: 'vehicle_status_history', description: 'Historical vehicle status and inspection records.' })
        cards.push({ name: 'Vehicle Sync History', route: 'vehicle_sync_history', description: 'Every vehicle status change made by the Fleetio, TBN calendar and DVIR syncs.' })
      }
      if (this.features.views_reports_converter_enabled) {
        cards.push({ name: 'Report Converter', route: 'report_converter', description: 'Convert report formats for third-party system compatibility.' })
      }
      if (!this.isTbn && this.features.vehicle_status_management_maint_summary_enabled) {
        cards.push({ name: 'Vehicle Maintenance Summary', route: 'vehicle_maint_summary', description: 'Summary of vehicle maintenance activities and costs.' })
      }
      if (this.isTbn && (this.user.tenant === 'mtrwestern-tbn-3db5da5g7')) {
        cards.push({ name: 'Hours By Client Report',  route: 'actuals_hours_by_client_report_tbn',  description: 'Total hours worked grouped by client.' })
        cards.push({ name: 'Hours By Booking Report', route: 'actuals_hours_by_booking_report_tbn', description: 'Hours breakdown per individual booking.' })
      }
      if (this.isTbn && (this.user.tenant === 'anderson-759g468f' || this.user.role === 'admin')) {
        cards.push({ name: 'Intrastate Booking Report', route: 'intrastate_booking_report_tbn', description: 'Report on intrastate travel bookings.' })
      }
      if (this.isTbn && (this.user.tenant === 'haletrans-31f47ab3' || this.user.role === 'admin')) {
        cards.push({ name: 'Daily Dispatch', route: 'hale_daily_dispatch_report_tbn', description: 'Daily dispatch sheet split by garage, with departing and already-out trips.' })
      }
      return cards
    },
  },
  watch: {
    visibleTabs: {
      immediate: true,
      handler(tabs) {
        if (tabs.length && !tabs.find(t => t.id === this.activeTab)) {
          this.activeTab = tabs[0].id
        }
      },
    },
  },
}
</script>

<style scoped>
/* ── Page wrapper ───────────────────────────────────────────────────── */
.rds-page {
  padding-bottom: 40px;
}

/* ── Category filter pills ──────────────────────────────────────────── */
.rds-pill-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.rds-pill {
  padding: 6px 18px;
  border-radius: 20px;
  border: 1px solid #c8d8e8;
  background: #f0f5fa;
  color: #4a6785;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  outline: none;
}

.rds-pill:hover {
  background: #dce8f5;
  border-color: var(--fi-rail-accent);
  color: var(--fi-rail-accent);
}

.rds-pill.active {
  background: var(--fi-rail-accent);
  border-color: var(--fi-rail-accent);
  color: #fff;
}

/* ── Section ────────────────────────────────────────────────────────── */
.rds-section {
  margin-bottom: 8px;
}

.rds-section + .rds-section {
  margin-top: 28px;
}

.rds-section-heading {
  font-size: 18px;
  font-weight: bold;
  color: #222;
  margin: 0 0 6px;
}

.rds-section-sub {
  color: #555;
  font-size: 14px;
  margin-bottom: 20px;
}

/* ── Card grid ──────────────────────────────────────────────────────── */
.rds-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  padding-right: 16px;
}

/* ── Card wrap (positions the star button) ──────────────────────────── */
.rds-card-wrap {
  position: relative;
}

.rds-star {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: #bbb;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s, transform 0.1s;
  outline: none;
}

.rds-star:hover {
  color: #e6b800;
  transform: scale(1.2);
}

.rds-star--active {
  color: #e6b800;
}

.rds-pill-star {
  margin-right: 3px;
  font-size: 12px;
}

/* ── Empty favorites state ──────────────────────────────────────────── */
.rds-empty-favorites {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: #888;
  text-align: center;
}

.rds-empty-star {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 12px;
  line-height: 1;
}

.rds-empty-favorites p {
  font-size: 14px;
  max-width: 320px;
  line-height: 1.5;
}

.rds-card {
  border: 1px solid #d0d8e4;
  border-radius: 6px;
  padding: 16px;
  padding-right: 36px;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(70,130,180,0.08);
  transition: box-shadow 0.15s, border-color 0.15s;
  cursor: pointer;
  text-decoration: none !important;
}

.rds-card:hover {
  border-color: var(--fi-rail-accent);
  box-shadow: 0 2px 8px rgba(70,130,180,0.18);
  text-decoration: none !important;
}

.rds-card-title {
  font-size: 15px;
  font-weight: bold;
  color: #222;
  margin-bottom: 6px;
}

.rds-card-desc {
  font-size: 13px;
  color: #666;
  flex: 1;
  line-height: 1.4;
}
</style>
